import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, RefreshCw, Loader2, Mail, Building2, ExternalLink, MessageSquare, Save, Send, AlertCircle, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const STATUS_META = {
  new:        { color: 'bg-sky-500/15 text-sky-300 border-sky-500/30', label: 'New' },
  contacted:  { color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', label: 'Contacted' },
  qualified:  { color: 'bg-violet-500/15 text-violet-300 border-violet-500/30', label: 'Qualified' },
  won:        { color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', label: 'Won' },
  lost:       { color: 'bg-rose-500/15 text-rose-300 border-rose-500/30', label: 'Lost' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-medium ${meta.color}`}>{meta.label}</span>;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ShareLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [edits, setEdits] = useState({}); // lead_id -> { status, notes }
  const [saving, setSaving] = useState({});
  const [sendingDigest, setSendingDigest] = useState(false);
  const [emailReady, setEmailReady] = useState(true); // optimistic; load on mount

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const data = await api.listShareLeads(params.toString());
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Could not load leads');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Detect whether SendGrid is configured so we can surface a banner that
  // links to the existing Settings → Notifications UI for the digest feature.
  useEffect(() => {
    api.getNotificationChannels()
      .then(ch => setEmailReady(!!ch?.email))
      .catch(() => setEmailReady(true));
  }, []);

  const counts = leads.reduce((acc, l) => { acc[l.status || 'new'] = (acc[l.status || 'new'] || 0) + 1; return acc; }, {});

  const updateField = (leadId, key, value) => {
    setEdits(e => ({ ...e, [leadId]: { ...(e[leadId] || {}), [key]: value } }));
  };

  const saveLead = async (lead) => {
    const draft = edits[lead.lead_id] || {};
    if (Object.keys(draft).length === 0) { toast.info('No changes'); return; }
    setSaving(s => ({ ...s, [lead.lead_id]: true }));
    try {
      await api.updateShareLead(lead.lead_id, draft);
      toast.success('Lead updated');
      setEdits(e => { const c = { ...e }; delete c[lead.lead_id]; return c; });
      await load();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(s => ({ ...s, [lead.lead_id]: false }));
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const r = await api.sendTrialLeadsDigest();
      if (r.status === 'sent') {
        toast.success(`Digest emailed to ${r.sent}/${r.recipients} admin(s) (${r.lead_count} leads)`);
      } else {
        toast.info(`Digest skipped: ${r.reason || r.status}`);
      }
    } catch (err) {
      toast.error(err.message || 'Could not send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="share-leads-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" /> Trial Leads
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Prospects who hit the PDF download paywall on a shared dashboard preview. Track follow-up status and notes here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={sendDigestNow} variant="outline" size="sm" className="rounded-lg" disabled={sendingDigest} data-testid="send-digest-btn" title="Send the weekly digest of new leads to tenant admins right now">
            {sendingDigest ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-2" />}
            Send digest now
          </Button>
          <Button onClick={load} variant="outline" size="sm" className="rounded-lg" data-testid="refresh-leads-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* SendGrid not configured banner — links directly to in-app settings */}
      {!emailReady && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="sendgrid-not-configured-banner">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground">SendGrid is not configured</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
              The weekly trial-leads digest and "Send digest now" button cannot deliver until you add a SendGrid API key and verified sender email.
            </p>
          </div>
          <Link to="/settings#notifications">
            <Button size="sm" variant="outline" className="rounded-lg text-[11px] h-8 flex-shrink-0" data-testid="configure-sendgrid-btn">
              <SettingsIcon className="w-3.5 h-3.5 mr-1.5" /> Configure SendGrid
            </Button>
          </Link>
        </div>
      )}

      {/* Status counts */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {['new', 'contacted', 'qualified', 'won', 'lost'].map(s => (
          <div key={s} className="glass-card rounded-2xl p-4" data-testid={`leads-count-${s}`}>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{STATUS_META[s].label}</p>
            <p className="text-2xl font-heading font-bold text-foreground mt-1">{counts[s] || 0}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">Status</span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-8 rounded-lg text-xs" data-testid="leads-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="qualified">Qualified</SelectItem>
            <SelectItem value="won">Won</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leads table */}
      <div className="glass-card rounded-2xl overflow-hidden">
        {loading && leads.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading leads…
          </div>
        ) : leads.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm" data-testid="leads-empty-state">
            No trial leads yet. Once viewers hit the PDF download paywall on a shared dashboard, they'll show up here.
          </div>
        ) : (
          <div className="divide-y divide-border/20">
            {leads.map(lead => {
              const isExpanded = expanded[lead.lead_id];
              const draft = edits[lead.lead_id] || {};
              const status = draft.status ?? lead.status ?? 'new';
              return (
                <div key={lead.lead_id} className="p-4" data-testid={`lead-row-${lead.lead_id}`}>
                  <div className="flex items-start justify-between gap-4 cursor-pointer" onClick={() => setExpanded(x => ({ ...x, [lead.lead_id]: !isExpanded }))}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-semibold text-foreground truncate">{lead.name || '(no name)'}</span>
                        <StatusBadge status={status} />
                        <span className="text-[10px] text-muted-foreground/70">{fmtTime(lead.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" /><a href={`mailto:${lead.email}`} className="text-foreground/80 hover:text-primary" onClick={e => e.stopPropagation()}>{lead.email}</a></span>
                        {lead.company && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /><span className="text-foreground/80">{lead.company}</span></span>}
                        {lead.resource_name && <span className="flex items-center gap-1"><ExternalLink className="w-3 h-3" /><span className="text-foreground/80 truncate max-w-[260px]">{lead.resource_name}</span></span>}
                      </div>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" onClick={e => e.stopPropagation()} data-testid={`lead-details-${lead.lead_id}`}>
                      <div>
                        <p className="text-[10px] text-muted-foreground mb-1">Status</p>
                        <Select value={status} onValueChange={v => updateField(lead.lead_id, 'status', v)}>
                          <SelectTrigger className="h-8 rounded-lg text-xs" data-testid={`lead-status-${lead.lead_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="qualified">Qualified</SelectItem>
                            <SelectItem value="won">Won</SelectItem>
                            <SelectItem value="lost">Lost</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Sales notes</p>
                        <textarea
                          value={draft.notes ?? lead.notes ?? ''}
                          onChange={e => updateField(lead.lead_id, 'notes', e.target.value)}
                          className="w-full rounded-lg bg-muted/30 border border-border/30 px-3 py-2 text-xs min-h-[60px] focus:ring-1 focus:ring-primary/50 focus:outline-none resize-none"
                          placeholder="Follow-up notes, call date, objections, etc."
                          data-testid={`lead-notes-${lead.lead_id}`}
                        />
                      </div>
                      <div className="sm:col-span-3 flex items-center justify-between text-[10px] text-muted-foreground">
                        <div className="flex flex-wrap items-center gap-3">
                          <span>Source: <span className="text-foreground/70">{lead.source || '—'}</span></span>
                          {lead.share_label && <span>Share: <span className="text-foreground/70">{lead.share_label}</span></span>}
                          {lead.updated_at && <span>Updated: <span className="text-foreground/70">{fmtTime(lead.updated_at)}</span></span>}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => saveLead(lead)}
                          disabled={!!saving[lead.lead_id] || Object.keys(edits[lead.lead_id] || {}).length === 0}
                          className="h-7 rounded-lg text-[11px]"
                          data-testid={`save-lead-${lead.lead_id}`}
                        >
                          {saving[lead.lead_id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
